import { IMortalityCalculations } from "@nkr/models/index";
import { Modal, Button, Label } from "react-bootstrap";
import { BreakdownWrapper } from "./styles";

interface IProps {
    show: boolean,
    recipientAlias: string,
    data: IMortalityCalculations,
    onClose: () => void
}

const MortalityBreakdown = (props: IProps) => {
    const toSuperscript = (str: string) => {
        const superscriptMap = {
            '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
            '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹'
        };

        return str.split('').map(char => superscriptMap[char] || char).join('');
    }

    if (props.data == null || props.show == false || props.data == null) {
        return <></>;
    }

    let lineD = '';

    if (props.data.hasDiabetes == false) {
        lineD = 'N/A';
    } else if (props.data.isOnDialysis && props.data.hasDiabetes) {
        lineD = 'If the patient is diabetic or we are unaware of their status, add 0.4974';
    } else {
        lineD = 'If the patient is diabetic or we are unaware of their status, add 0.6221';
    }

    return (
        <>
            <Modal backdrop='static' bsSize='large' show={props.show} onHide={props.onClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Recipient {props.recipientAlias} Mortality Calculation Breakdown</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <BreakdownWrapper bordered condensed>
                        <thead>
                            <tr>
                                <th></th>
                                <th style={{ width: '28%' }}>Summary</th>
                                <th>Input</th>
                                <th>Calculation</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td></td>
                                <td className='rowLabel' style={{ textAlign: 'left' }}>Years on dialysis</td>
                                <td style={{ textAlign: 'center' }}><div className='breakdown-label'>{props.data.yearsOnDialysis.toFixed(1)}</div></td>
                                <td>(used in E &amp; F below)</td>
                                <td>{props.data.yearsOnDialysis.toFixed(1)}</td>
                            </tr>
                            <tr>
                                <td>(a)</td>
                                <td className='rowLabel' style={{ textAlign: 'left' }}>Patient on dialysis?</td>
                                <td style={{ textAlign: 'center' }}><div className='breakdown-label'>{props.data.isOnDialysis ? 'Yes' : 'No'}</div></td>
                                <td>Constant</td>
                                <td style={{ textAlign: 'center' }}>{props.data.constantA.toFixed(4)}</td>
                            </tr>
                            <tr>
                                <td>(b)</td>
                                <td className='rowLabel' style={{ textAlign: 'left' }}>logScale</td>
                                <td style={{ textAlign: 'center' }}>{props.data.constantB.toFixed(4)}</td>
                                <td>Constant</td>
                                <td style={{ textAlign: 'center' }}>{props.data.constantB.toFixed(4)}</td>
                            </tr>
                            <tr>
                                <td>(c)</td>
                                <td className='rowLabel' style={{ textAlign: 'left' }}>Age in years</td>
                                <td style={{ textAlign: 'center' }}><div className='breakdown-label'>{props.data.age}</div></td>
                                <td style={{ textAlign: 'center' }}>{'Age * '}{props.data.constantC.toFixed(4)}</td>
                                <td style={{ textAlign: 'center' }}>{props.data.c.toFixed(4)}</td>
                            </tr>
                            <tr>
                                <td>(d)</td>
                                <td className='rowLabel' style={{ textAlign: 'left' }}>Does the patient have diabetes?</td>
                                <td style={{ textAlign: 'center' }}><div className='breakdown-label'>{props.data.hasDiabetes == true ? 'Yes' : props.data.hasDiabetes == false ? 'No' : 'UNK'}</div></td>
                                <td>{lineD}</td>
                                <td style={{ textAlign: 'center' }}>{props.data.d.toFixed(4)}</td>
                            </tr>
                            <tr>
                                <td>(e)</td>
                                <td className='rowLabel' style={{ textAlign: 'left' }}>Impact of dialysis, up to two years</td>
                                <td style={{ textAlign: 'center' }}>{props.data.yearsOnDialysis.toFixed(1)}</td>
                                <td style={{ textAlign: 'center' }}>{props.data.constantE.toFixed(4)}{props.data.yearsOnDialysis > 2 ? ` + ${props.data.constantE.toFixed(4)}` : ''}</td>
                                <td style={{ textAlign: 'center' }}>{props.data.e.toFixed(4)}</td>
                            </tr>
                            <tr>
                                <td>(f)</td>
                                <td className='rowLabel' style={{ textAlign: 'left' }}>Impact of dialysis, over two years</td>
                                <td style={{ textAlign: 'center' }}>{(props.data.yearsOnDialysis - 2 < 0 ? 0 : props.data.yearsOnDialysis).toFixed(1)}</td>
                                <td style={{ textAlign: 'center' }}>{props.data.yearsOnDialysis > 2 ? <>{props.data.constantF} * (years on dialysis - 2)</> : <>N/A</>}</td>
                                <td style={{ textAlign: 'center' }}>{props.data.f.toFixed(4)}</td>
                            </tr>
                            <tr>
                                <td>(t)</td>
                                <td className='rowLabel' style={{ textAlign: 'left' }}>Survival years to calculate </td>
                                <td style={{ textAlign: 'center' }}>{props.data.t}</td>
                                <td>N/A</td>
                                <td style={{ textAlign: 'center' }}>{props.data.t}</td>
                            </tr>
                            <tr>
                                <td>(s)</td>
                                <td className='rowLabel' style={{ textAlign: 'left' }}>exp(a)</td>
                                <td>(a)</td>
                                <td style={{ textAlign: 'center' }}>{`exp(${props.data.constantA.toFixed(4)})`}</td>
                                <td style={{ textAlign: 'center' }}>{props.data.s.toFixed(4)}</td>
                            </tr>
                            <tr>
                                <td>(m)</td>
                                <td className='rowLabel' style={{ textAlign: 'left' }}>exp(b + c + d + e + f)</td>
                                <td>(b, c, d, e, f)</td>
                                <td style={{ textAlign: 'center' }}>{`exp(${props.data.mPreExponent.toFixed(4)})`}</td>
                                <td style={{ textAlign: 'center' }}>{props.data.m.toFixed(4)}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={4}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div className='breakdown-label' style={{ width: 50 }}>&nbsp;</div>&nbsp;<div>- from the recipient profile</div>
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </BreakdownWrapper>

                    <BreakdownWrapper bordered condensed style={{ width: 'initial' }}>
                        <tbody>
                            <tr>
                                <td className='rowLabel set-height' style={{ backgroundColor: '#ddd' }}>Survival Chance Formula</td>
                                <td style={{ width: 270 }}>{'z(t) = exp((m * -1) * (t'}<span style={{ fontSize: 18 }}>{'\u02E2'}</span>{'))'}</td>
                            </tr>
                            <tr>
                                <td className='rowLabel set-height'></td>
                                <td>{`z(t) = exp((${props.data.m.toFixed(4)} * -1) * (${props.data.t.toFixed(0)}`}<span style={{ fontSize: 18 }}>{toSuperscript(props.data.s.toFixed(4))}</span>{'))'}</td>
                            </tr>
                            <tr>
                                <td className='rowLabel set-height'></td>
                                <td>{`z(t) = ${(props.data.chanceOfSurvival / 100).toFixed(4)}`}</td>
                            </tr>

                            <tr>
                                <td className='rowLabel set-height' style={{ backgroundColor: '#ddd' }}>1-Year Mortality Risk</td>
                                <td>{`100% - ${(props.data.chanceOfSurvival).toFixed(0)}% = `}</td>
                                <td style={{ backgroundColor: 'yellow', fontSize: 18 }}>{props.data.riskPercent.toFixed(0)}%</td>
                            </tr>
                        </tbody>
                    </BreakdownWrapper>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={props.onClose}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default MortalityBreakdown;